import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MembershipService } from '../../core/services/membership.service';
import { AdminMemberView } from '../../core/models/membership.model';

/**
 * Assign Chief Supervisor Modal Component
 *
 * Modal for assigning or removing Chief Supervisor role.
 * Only one Chief Supervisor allowed per organization.
 *
 * Usage:
 * ```html
 * <app-assign-chief-supervisor-modal
 *   [organizationId]="orgId"
 *   [members]="membersList"
 *   [currentChiefSupervisorId]="csId"
 *   (close)="onModalClose()"
 *   (chiefSupervisorAssigned)="onChiefSupervisorAssigned()">
 * </app-assign-chief-supervisor-modal>
 * ```
 */
@Component({
  selector: 'app-assign-chief-supervisor-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  template: `
    <div class="modal-backdrop fade show" (click)="onClose()"></div>
    <div class="modal fade show d-block" tabindex="-1" role="dialog" aria-modal="true">
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Assign Chief Supervisor</h5>
            <button type="button" class="btn-close" (click)="onClose()" aria-label="Close"></button>
          </div>
          <div class="modal-body">
            <!-- Error Message -->
            <div class="alert alert-danger" *ngIf="errorMessage">
              <i class="bi bi-exclamation-triangle-fill me-2"></i>
              {{ errorMessage }}
            </div>

            <!-- Success Message -->
            <div class="alert alert-success" *ngIf="successMessage">
              <i class="bi bi-check-circle-fill me-2"></i>
              {{ successMessage }}
            </div>

            <!-- Info -->
            <div class="alert alert-info">
              <i class="bi bi-info-circle-fill me-2"></i>
              <strong>Chief Supervisor Role:</strong> Responsible for overall operational safety
              and compliance. Only one Chief Supervisor per organization.
            </div>

            <!-- Current Chief Supervisor -->
            <div class="mb-4" *ngIf="currentChiefSupervisor">
              <label class="form-label fw-bold">Current Chief Supervisor</label>
              <div class="border rounded p-3 bg-body-tertiary">
                <div class="d-flex align-items-center">
                  <div class="entity-avatar bg-success text-white fs-5 me-3">
                    <i class="bi bi-shield-fill"></i>
                  </div>
                  <div>
                    <div class="fw-bold">{{ currentChiefSupervisor.firstName }} {{ currentChiefSupervisor.lastName }}</div>
                    <small class="text-muted">{{ currentChiefSupervisor.email }}</small>
                  </div>
                  <span class="badge bg-success ms-auto">Chief Supervisor</span>
                </div>
              </div>
            </div>

            <div class="mb-4" *ngIf="!currentChiefSupervisor">
              <div class="alert alert-warning">
                <i class="bi bi-exclamation-triangle-fill me-2"></i>
                No Chief Supervisor is currently assigned to this organization.
              </div>
            </div>

            <!-- No Eligible Members Message -->
            <div class="alert alert-info" *ngIf="eligibleMembers.length === 0 && !currentChiefSupervisor">
              <i class="bi bi-info-circle-fill me-2"></i>
              <strong>No eligible members available.</strong>
              <p class="mb-0 mt-1">
                Chief Supervisor can only be assigned to members who have accepted their invitation and created an account.
                Please ensure at least one member has completed their registration.
              </p>
            </div>

            <!-- New Chief Supervisor Selection -->
            <div class="mb-3" *ngIf="eligibleMembers.length > 0 || currentChiefSupervisor">
              <label for="newCS" class="form-label fw-bold">
                {{ currentChiefSupervisor ? 'Change Chief Supervisor' : 'Assign Chief Supervisor' }}
              </label>
              <select
                class="form-select"
                id="newCS"
                [(ngModel)]="selectedChiefSupervisorId"
                [disabled]="saving"
              >
                <option [ngValue]="null">-- Remove Chief Supervisor --</option>
                <option
                  *ngFor="let member of eligibleMembers"
                  [value]="member.profileId"
                >
                  {{ member.firstName }} {{ member.lastName }} ({{ member.email }})
                  <span *ngIf="member.isOwner"> - Owner</span>
                </option>
              </select>
              <small class="form-text text-muted">
                Select a member to assign as Chief Supervisor, or choose "Remove" to clear the assignment.
              </small>
            </div>

            <!-- Change Summary -->
            <div class="alert alert-info" *ngIf="hasChanges()">
              <h6 class="alert-heading">Change Summary</h6>
              <div *ngIf="selectedChiefSupervisorId">
                <strong>New Chief Supervisor:</strong> {{ getSelectedMemberName() }}
              </div>
              <div *ngIf="!selectedChiefSupervisorId && currentChiefSupervisor">
                <strong>Action:</strong> Remove Chief Supervisor assignment
                <br>
                <small class="text-muted">
                  {{ currentChiefSupervisor.firstName }} {{ currentChiefSupervisor.lastName }}
                  will no longer be Chief Supervisor but will retain other roles.
                </small>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" (click)="onClose()" [disabled]="saving">
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              (click)="onAssign()"
              [disabled]="saving || !hasChanges()"
            >
              <span *ngIf="saving" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ saving ? 'Saving...' : (selectedChiefSupervisorId ? 'Assign Chief Supervisor' : 'Remove Chief Supervisor') }}
            </button>
          </div>
        </div>
      </div>
    </div>
  `,
})
export class AssignChiefSupervisorModalComponent implements OnInit {
  @Input() organizationId!: string;
  @Input() members: AdminMemberView[] = [];
  @Input() currentChiefSupervisorId?: string | null;
  @Output() close = new EventEmitter<void>();
  @Output() chiefSupervisorAssigned = new EventEmitter<void>();

  selectedChiefSupervisorId: string | null = null;
  saving = false;
  errorMessage = '';
  successMessage = '';

  currentChiefSupervisor?: AdminMemberView;
  eligibleMembers: AdminMemberView[] = [];

  constructor(private membershipService: MembershipService) {}

  ngOnInit(): void {
    // Find current CS if exists
    if (this.currentChiefSupervisorId) {
      this.currentChiefSupervisor = this.members.find(
        m => m.profileId === this.currentChiefSupervisorId
      );
      this.selectedChiefSupervisorId = this.currentChiefSupervisorId;
    }

    // Eligible members are active members who have accepted their invite (have a Firebase account).
    // Treat missing hasAcceptedInvite as accepted for backwards compatibility.
    this.eligibleMembers = this.members.filter(m =>
      m.isActive && m.hasAcceptedInvite !== false
    );
  }

  /**
   * Check if selection has changed
   */
  hasChanges(): boolean {
    return this.selectedChiefSupervisorId !== this.currentChiefSupervisorId;
  }

  /**
   * Get selected member name
   */
  getSelectedMemberName(): string {
    if (!this.selectedChiefSupervisorId) return '';
    const member = this.members.find(m => m.profileId === this.selectedChiefSupervisorId);
    return member ? `${member.firstName} ${member.lastName}` : '';
  }

  /**
   * Assign or remove Chief Supervisor
   */
  async onAssign(): Promise<void> {
    this.errorMessage = '';
    this.successMessage = '';

    if (!this.hasChanges()) {
      this.errorMessage = 'No changes to save';
      return;
    }

    this.saving = true;

    try {
      await this.membershipService.assignChiefSupervisor(
        this.organizationId,
        this.selectedChiefSupervisorId
      );

      if (this.selectedChiefSupervisorId) {
        this.successMessage = 'Chief Supervisor assigned successfully';
      } else {
        this.successMessage = 'Chief Supervisor removed successfully';
      }

      // Wait briefly to show success message, then emit event and close
      setTimeout(() => {
        this.chiefSupervisorAssigned.emit();
        this.onClose();
      }, 1500);
    } catch (error: any) {
      console.error('Failed to assign Chief Supervisor:', error);
      this.errorMessage = error.message || 'Failed to update Chief Supervisor assignment';
      this.saving = false;
    }
  }

  /**
   * Close modal
   */
  onClose(): void {
    this.close.emit();
  }
}
